import { z } from 'zod';
import { PERMISSION_RANK, } from './core.js';
/**
 * Xác thực hai bước (TOTP) — **ai bắt buộc phải bật**.
 *
 * Rà soát bảo mật 16/10/2026 mục **C3**, vá đợt 8b. Một hàm duy nhất cho cả
 * máy chủ (hook chặn ở `plugins/auth.ts`) và giao diện (màn ép bật ở
 * `lib/guard.tsx`): hai bản sao của luật này là hai bản sẽ lệch nhau, và bản
 * lệch về phía *lỏng* thì không ai nhìn thấy.
 */
/**
 * Số mã dự phòng sinh mỗi lượt.
 *
 * Ở `shared` vì **cả hai phía** nói con số này ra thành chữ: máy chủ sinh đúng
 * bấy nhiêu mã, giao diện viết "lưu 10 mã dự phòng" trong lời hướng dẫn. Hai
 * bản sao là một ngày nào đó màn hình dạy người dùng đếm sai.
 */
export const SO_MA_DU_PHONG = 10;
/** Còn **≤** số này thì giao diện nhắc sinh lại. */
export const NGUONG_NHAC_SINH_LAI = 3;
/* ------------------------------------------------------------------ */
/* Luật bắt buộc                                                      */
/* ------------------------------------------------------------------ */
/**
 * Tài khoản này **phải** bật xác thực hai bước hay không.
 *
 * Hai điều kiện, thoả **một** là bắt buộc:
 *
 * 1. **Có bất kỳ module ở mức `admin`.** Mức đó là mức đổi được cấu hình của
 *    cả công ty: `core:admin` và `team:admin` tạo người dùng và sửa ma trận
 *    quyền, `revenue:admin` chốt dòng tiền, `channels:admin` đổi phân công.
 * 2. **Phạm vi dữ liệu `toan_cong_ty`.** Không đổi được gì nhưng **thấy** mọi
 *    thứ: Kế toán có `revenue: edit` mà vẫn đọc doanh thu của cả công ty và hồ
 *    sơ thuế của từng nhân sự (CCCD, MST, số tài khoản). Một tài khoản như thế
 *    bị chiếm là mất hết dữ liệu công ty, dù kẻ chiếm không sửa nổi một dòng.
 *
 * Cố ý **không** xét khoá vai trò (`roles.key === 'admin'`): ma trận quyền sửa
 * được từ giao diện, nên một vai trò tên bất kỳ vẫn có thể được bật `core:
 * admin` — và lúc đó một luật theo tên vai trò lặng lẽ bỏ sót. Đúng cái lỗi
 * "cờ ngầm" mà `PhamViDuLieu` đã phải sửa hồi 17/10/2026.
 *
 * **Fail-closed**: thiếu `permissions` (chưa tải xong `/core/me`, hay đọc vai
 * trò lỗi) thì `gopPhamViDuLieu([])` đã trả `kenh_duoc_giao`, và ở đây bản đồ
 * quyền rỗng cho ra `false` — tức *không* ép bật. Chiều an toàn ở hàm này là
 * chiều **ngược** với các cổng quyền khác: đoán sai về phía "bắt buộc" thì
 * người dùng bị kẹt ở màn ép bật mà họ không cần, đoán sai về phía kia thì chỉ
 * chậm một nhịp rồi hook máy chủ chặn lại. Vì vậy gọi hàm này **chỉ khi đã
 * biết** quyền thật.
 */
export function canXacThucHaiBuoc(permissions, phamViDuLieu) {
    if (phamViDuLieu === 'toan_cong_ty')
        return true;
    if (!permissions)
        return false;
    return Object.values(permissions).some((level) => PERMISSION_RANK[level] >= PERMISSION_RANK.admin);
}
/* ------------------------------------------------------------------ */
/* Bật 2FA: hai bước                                                  */
/* ------------------------------------------------------------------ */
/**
 * Đáp án của `POST /core/2fa/bat-dau` — thứ cần để vẽ màn quét QR.
 *
 * `secret` trả về dạng rõ **cố ý**: nó là bí mật của chính người đang gọi, và
 * họ phải chuyển được nó sang điện thoại. `qrSvg` là cùng secret đó ở dạng ảnh;
 * `secretDeDoc` là cùng secret ở dạng nhập tay khi điện thoại không quét được.
 */
export const batDau2faResponseSchema = z.object({
    /** Secret base32, 32 ký tự. */
    secret: z.string(),
    /** Cùng secret, chia nhóm 4 ký tự để nhập tay. */
    secretDeDoc: z.string(),
    /** URI `otpauth://` — nội dung của mã QR. */
    uri: z.string(),
    /** Mã QR dạng SVG, nhúng thẳng vào trang. Không ảnh ngoài, không script. */
    qrSvg: z.string(),
    /**
     * Phiếu của lượt bật này: JWT ngắn hạn **chở luôn secret**, phải gửi lại ở
     * bước xác nhận.
     *
     * Secret chờ xác nhận nằm trong phiếu chứ không trong database: không thêm
     * một cột chỉ để giữ trạng thái nửa đường, và không để lại secret rác của
     * những lượt bật bỏ giữa đường.
     */
    phieu: z.string(),
});
/** Mã TOTP người dùng nhập: đúng 6 chữ số, cho phép dấu cách ở giữa. */
export const maTotpSchema = z
    .string()
    .trim()
    .transform((s) => s.replace(/\s/g, ''))
    .pipe(z.string().regex(/^\d{6}$/, 'Mã gồm 6 chữ số'));
export const xacNhan2faInputSchema = z.object({
    phieu: z.string().min(1, 'Thiếu phiếu của lượt bật'),
    ma: maTotpSchema,
});
/**
 * Đáp án khi bật thành công, và cũng là đáp án của lượt sinh lại mã dự phòng.
 *
 * `maDuPhong` là lần **duy nhất** mười mã hiện ra dạng rõ: database chỉ giữ
 * chuỗi băm SHA-256 của chúng.
 */
export const maDuPhongResponseSchema = z.object({
    maDuPhong: z.array(z.string()),
});
/** Tắt 2FA, hoặc sinh lại mã dự phòng: cả hai đòi nhập lại mật khẩu hiện tại. */
export const xacNhanMatKhauInputSchema = z.object({
    currentPassword: z.string().min(1, 'Chưa nhập mật khẩu hiện tại'),
});
/* ------------------------------------------------------------------ */
/* Đăng nhập hai bước                                                 */
/* ------------------------------------------------------------------ */
/**
 * Mã ở bước hai: hoặc 6 chữ số TOTP, hoặc một mã dự phòng
 * (`XXXXX-XXXXX`). Một ô nhập cho cả hai — người vừa mất điện thoại không phải
 * đi tìm xem gõ mã dự phòng vào đâu.
 *
 * Chỉ kiểm độ dài tối thiểu ở đây; phân loại và kiểm thật là việc của máy chủ.
 */
export const dangNhap2faInputSchema = z.object({
    phieu: z.string().min(1, 'Thiếu phiếu của bước một'),
    ma: z.string().trim().min(6, 'Chưa nhập mã'),
});
/**
 * Đáp án của `POST /core/auth/login` khi tài khoản **đã bật** 2FA.
 *
 * Lượt đó **không phát cookie phiên nào**: mật khẩu đúng chỉ mở ra bước hai.
 * `can2fa: true` là cờ để giao diện phân biệt với một `MeResponse` bình
 * thường.
 */
export const can2faResponseSchema = z.object({
    can2fa: z.literal(true),
    /** JWT `typ: '2fa'`, hạn 5 phút. */
    phieu: z.string(),
    /** Còn bao nhiêu mã dự phòng chưa dùng — để nhắc đúng lúc người dùng cần. */
    maDuPhongConLai: z.number().int().min(0),
});
/** Đáp án của `/auth/login`: hoặc đăng nhập xong, hoặc còn bước hai. */
export function canBuocHai(du) {
    return typeof du === 'object' && du !== null && du.can2fa === true;
}
/* ------------------------------------------------------------------ */
/* Trạng thái cho giao diện                                           */
/* ------------------------------------------------------------------ */
/** Trạng thái 2FA của chính mình, cho trang "Tài khoản của tôi". */
export const trangThai2faSchema = z.object({
    daBat: z.boolean(),
    /** Có thuộc nhóm **bắt buộc** hay không — quyết định có cho tắt hay không. */
    batBuoc: z.boolean(),
    batLuc: z.iso.datetime().nullable(),
    maDuPhongConLai: z.number().int().min(0),
});
/** Người này đang ở trạng thái **bắt buộc mà chưa bật** hay không. */
export function dangThieu2fa(user, permissions, phamViDuLieu) {
    if (!user)
        return false;
    if (user.twoFactorEnabled)
        return false;
    return canXacThucHaiBuoc(permissions, phamViDuLieu);
}
//# sourceMappingURL=haiBuoc.js.map